import { useEffect } from "react";

export const useRetrospectiveSocketListeners = (
  connected: boolean,
  on: any,
  setNodes: any,
  setEdges: any,
  reactFlowInstance: any,
  userId: string
) => {
  useEffect(() => {
    if (!connected) return;

    const unsubscribeNodeAdded = on("NODE_ADDED", ({ node, addedBy }: any) => {
      if (addedBy !== userId) {
        setNodes((nds: any[]) => [...nds, node]);
      }
    });

    const unsubscribeNodePositionUpdated = on(
      "NODE_POSITION_UPDATED",
      ({ nodeId, position, updatedBy }: any) => {
        if (updatedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId ? { ...node, position } : node
            )
          );
        }
      }
    );

    const unsubscribeNodeColorUpdated = on(
      "NODE_COLOR_UPDATED",
      ({ nodeId, color, updatedBy }: any) => {
        if (updatedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? { ...node, data: { ...node.data, color } }
                : node
            )
          );
        }
      }
    );

    const unsubscribeNodeTextUpdated = on(
      "NODE_TEXT_UPDATED",
      ({ nodeId, text, updatedBy }: any) => {
        if (updatedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? { ...node, data: { ...node.data, text } }
                : node
            )
          );
        }
      }
    );

    const unsubscribeNodeTextChanged = on(
      "NODE_TEXT_CHANGED",
      ({ nodeId, text, userId: senderId }: any) => {
        if (senderId !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? { ...node, data: { ...node.data, text } }
                : node
            )
          );
        }
      }
    );

    const unsubscribeNodeDimensionsUpdated = on(
      "NODE_DIMENSIONS_UPDATED",
      ({ nodeId, dimensions, updatedBy }: any) => {
        if (updatedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      width: dimensions.width,
                      height: dimensions.height,
                    },
                    style: {
                      ...node.style,
                      width: dimensions.width,
                      height: dimensions.height,
                    },
                  }
                : node
            )
          );
        }
      }
    );

    const unsubscribeNodeDeleted = on(
      "NODE_DELETED",
      ({ nodeId, deletedBy }: any) => {
        if (deletedBy !== userId) {
          setNodes((nds: any[]) => nds.filter((node) => node.id !== nodeId));
        }
      }
    );

    const unsubscribeEdgeAdded = on("EDGE_ADDED", ({ edge, addedBy }: any) => {
      if (addedBy !== userId) {
        setEdges((eds: any[]) => [...eds, edge]);
      }
    });

    const unsubscribeEdgeDeleted = on(
      "EDGE_DELETED",
      ({ edgeId, deletedBy }: any) => {
        if (deletedBy !== userId) {
          setEdges((eds: any[]) => eds.filter((edge) => edge.id !== edgeId));
        }
      }
    );

    const unsubscribeViewportChanged = on(
      "VIEWPORT_CHANGED",
      ({ viewport, changedBy }: any) => {
        if (changedBy !== userId && reactFlowInstance) {
          reactFlowInstance.setViewport(viewport, { duration: 200 });
        }
      }
    );

    const unsubscribeEmojiReactionAdded = on(
      "EMOJI_REACTION_ADDED",
      ({ nodeId, reaction, addedBy }: any) => {
        if (addedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      reactions: [...(node.data.reactions || []), reaction],
                    },
                  }
                : node
            )
          );
        }
      }
    );

    const unsubscribeEmojiReactionRemoved = on(
      "EMOJI_REACTION_REMOVED",
      ({ nodeId, emoji, userId: reactionUserId, removedBy }: any) => {
        if (removedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      reactions: (node.data.reactions || []).filter(
                        (reaction: any) =>
                          !(
                            reaction.emoji === emoji &&
                            reaction.userId === reactionUserId
                          )
                      ),
                    },
                  }
                : node
            )
          );
        }
      }
    );

    const unsubscribeCommentAdded = on(
      "COMMENT_ADDED",
      ({ nodeId, comment, addedBy }: any) => {
        if (addedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      comments: [...(node.data.comments || []), comment],
                    },
                  }
                : node
            )
          );
        }
      }
    );

    const unsubscribeCommentReactionAdded = on(
      "COMMENT_REACTION_ADDED",
      ({ nodeId, commentId, reaction, addedBy }: any) => {
        if (addedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      comments: (node.data.comments || []).map((comment: any) =>
                        comment.id === commentId
                          ? {
                              ...comment,
                              reactions: [
                                ...(comment.reactions || []),
                                reaction,
                              ],
                            }
                          : comment
                      ),
                    },
                  }
                : node
            )
          );
        }
      }
    );

    const unsubscribeCommentReactionRemoved = on(
      "COMMENT_REACTION_REMOVED",
      ({
        nodeId,
        commentId,
        emoji,
        userId: reactionUserId,
        removedBy,
      }: any) => {
        if (removedBy !== userId) {
          setNodes((nds: any[]) =>
            nds.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      comments: (node.data.comments || []).map((comment: any) =>
                        comment.id === commentId
                          ? {
                              ...comment,
                              reactions: (comment.reactions || []).filter(
                                (reaction: any) =>
                                  !(
                                    reaction.emoji === emoji &&
                                    reaction.userId === reactionUserId
                                  )
                              ),
                            }
                          : comment
                      ),
                    },
                  }
                : node
            )
          );
        }
      }
    );

    return () => {
      unsubscribeNodeAdded();
      unsubscribeNodePositionUpdated();
      unsubscribeNodeColorUpdated();
      unsubscribeNodeTextUpdated();
      unsubscribeNodeTextChanged();
      unsubscribeNodeDimensionsUpdated();
      unsubscribeNodeDeleted();
      unsubscribeEdgeAdded();
      unsubscribeEdgeDeleted();
      unsubscribeViewportChanged();
      unsubscribeEmojiReactionAdded();
      unsubscribeEmojiReactionRemoved();
      unsubscribeCommentAdded();
      unsubscribeCommentReactionAdded();
      unsubscribeCommentReactionRemoved();
    };
  }, [connected, on, setNodes, setEdges, reactFlowInstance, userId]);
};
